import { ComponentType } from "@angular/cdk/portal";
import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom, Subject, take, takeUntil } from "rxjs";

import { ModalRef } from "@bitwarden/angular/components/modal/modal.ref";
import { ModalService } from "@bitwarden/angular/services/modal.service";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { PolicyService } from "@bitwarden/common/abstractions/policy/policy.service.abstraction";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { TokenService } from "@bitwarden/common/abstractions/token.service";
import { PolicyType } from "@bitwarden/common/enums/policyType";
import { TwoFactorProviderType } from "@bitwarden/common/enums/twoFactorProviderType";
import { TwoFactorProviders } from "@bitwarden/common/services/twoFactor.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { LoginDeviceApi } from "@bitwarden/web-vault/app/services/DataService/login-device/login-device.api";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ActivatedRoute, Router } from "@angular/router";
import {
  BiometricIcon,
  biometryTypeToIcon,
  checkBiometric,
  deleteCredentials,
  isCredentialsSaved,
  migrateLegacyFaceIdCredential,
} from "@bitwarden/web-vault/app/capacitor-plugins/native-biometric";
import { LogService } from "@bitwarden/common/abstractions/log.service";

import { LoginDeviceResponse } from "@bitwarden/web-vault/app/models/interfaces/login-device.interface";
import { ToggleSwitchComponent } from "@bitwarden/web-vault/app/components/toggle-switch/toggle-switch.component";

import { BiometricSetupHandoffService } from "../login/biometric-setup-handoff.service";
import {
  TwoFactorWebAuthnComponent,
  TwoFactorWebAuthnDialogData,
} from "./two-factor-webauthn.component";
import { mobileSlideInConfig } from "@bitwarden/web-vault/app/components/dialog/mobile-slide-in-dialog";

export type BiometricReconcileAction = "push-baseline" | "clear-local" | "no-op";

/**
 * Decides what reconcileBiometricStateWithServer should do given this device's current row (if
 * any) in gloss-api's device list.
 *
 * gloss-api only ever creates a device row in response to this device explicitly pushing its
 * state (`PUT /devices/me/biometric`) — there is no login-time row creation, so a row can never
 * exist with a "default" `biometricEnabled` that wasn't set by an explicit push (this device's
 * own, or Desktop's remote disable, which itself 404s against a device that hasn't pushed yet).
 * That makes the server the reliable source of truth: no matching row means this device has never
 * synced, so it's safe to push local state as the initial baseline; a matching row means its
 * `biometricEnabled` value is always authoritative and must be honored, even if it disagrees with
 * local state.
 */
export function resolveBiometricReconcileAction(
  currentDevice: LoginDeviceResponse | undefined,
): BiometricReconcileAction {
  if (!currentDevice) {
    return "push-baseline";
  }
  return currentDevice.biometricEnabled ? "no-op" : "clear-local";
}

/** Desktop's "other devices" list excludes this device's own row — it's shown separately above. */
export function filterOtherDevices(
  devices: LoginDeviceResponse[],
  currentDeviceId: string | undefined,
): LoginDeviceResponse[] {
  return devices.filter((d) => d.deviceId !== currentDeviceId);
}

/**
 * The mobile-first onboarding flow (reached via the login page's fingerprint button, mobile
 * only) is driven by a `setupBiometrics` query param with no device check of its own — its
 * "enable" button calls manage() unconditionally. Requiring isMobileDevice here too closes that
 * off for a desktop user navigating to the URL directly, which would otherwise reach the real
 * WebAuthn PRF enrollment ceremony outside the (separately gated) settings toggle.
 */
export function resolveIsMobileFirstSetup(
  setupBiometricsQueryParam: unknown,
  isMobileDevice: boolean,
): boolean {
  return setupBiometricsQueryParam === "true" && isMobileDevice;
}

@Component({
  selector: "app-two-factor-setup",
  templateUrl: "two-factor-setup.component.html",
})
export class TwoFactorSetupComponent implements OnInit, OnDestroy {
  protected apiService: ApiService;
  private confirmationDialogService: ConfirmationDialogService;
  private dialog: MatDialog;
  private i18nService: I18nService;
  protected modalService: ModalService;
  private platformUtilsService: PlatformUtilsService;
  protected policyService: PolicyService;
  private stateService: StateService;
  private tokenService: TokenService;
  private loginDeviceApi: LoginDeviceApi;
  private route: ActivatedRoute;
  protected logService: LogService;
  protected router: Router;
  private biometricSetupHandoff: BiometricSetupHandoffService;

  @ViewChild("webAuthnTemplate", { read: ViewContainerRef, static: true })
  webAuthnModalRef: ViewContainerRef;

  organizationId: string;
  providers: any[] = [];
  showPolicyWarning = false;
  loading = true;
  // Biometric enable/disable awaits this so a tap during the first post-upgrade launch can't
  // write the generic keychain slot while the legacy Face ID migration is still copying into it.
  private legacyFaceIdMigration: Promise<void> = Promise.resolve();
  modal: ModalRef;
  formPromise: Promise<any>;
  enableBiometricsOptions: ActionButton;
  cancelButtonOptions: ActionButton;
  isMobileFirstSetup = false;
  enableToggle = false;
  /** The WebAuthn setup dialog the enable path opens, while it is open. */
  private biometricSetupDialog?: MatDialogRef<unknown>;
  deviceType: string;
  isMobileDevice: boolean;
  otherDevices: LoginDeviceResponse[] = [];
  biometricIcon: BiometricIcon = "fingerprint";

  private destroy$ = new Subject<void>();
  private twoFactorAuthPolicyAppliesToActiveUser: boolean;

  constructor(injector: Injector) {
    this.apiService = injector.get(ApiService);
    this.confirmationDialogService = injector.get(ConfirmationDialogService);
    this.dialog = injector.get(MatDialog);
    this.i18nService = injector.get(I18nService);
    this.modalService = injector.get(ModalService);
    this.platformUtilsService = injector.get(PlatformUtilsService);
    this.policyService = injector.get(PolicyService);
    this.stateService = injector.get(StateService);
    this.tokenService = injector.get(TokenService);
    this.loginDeviceApi = injector.get(LoginDeviceApi);
    this.route = injector.get(ActivatedRoute);
    this.logService = injector.get(LogService);
    this.router = injector.get(Router);
    this.biometricSetupHandoff = injector.get(BiometricSetupHandoffService);
    this.enableBiometricsOptions = new ActionButton({
      text: this.i18nService.t("enable"),
      onClick: () => this.enableAvailableBiometrics(),
    });
    this.cancelButtonOptions = new ActionButton({
      class: "neutral",
      text: this.i18nService.t("laterInSettings"),
      onClick: () => this.goBackToVault(),
    });
    this.deviceType = this.platformUtilsService.getDeviceString();
    this.isMobileDevice = this.platformUtilsService.isMobileDevice();
  }

  async ngOnInit() {
    this.legacyFaceIdMigration = this.migrateLegacyFaceIdCredentialIfNeeded();
    await Promise.all([this.legacyFaceIdMigration, this.detectBiometricIcon()]);

    this.enableToggle = await isCredentialsSaved();
    this.isMobileFirstSetup = resolveIsMobileFirstSetup(
      this.route.snapshot.queryParams["setupBiometrics"],
      this.isMobileDevice,
    );

    await this.reconcileBiometricStateWithServer();
    await this.loadOtherDevices();

    for (const key in TwoFactorProviders) {
      // eslint-disable-next-line
      if (!TwoFactorProviders.hasOwnProperty(key)) {
        continue;
      }

      const p = (TwoFactorProviders as any)[key];
      if (this.filterProvider(p.type)) {
        continue;
      }

      this.providers.push({
        type: p.type,
        name: p.name,
        description: p.description,
        enabled: false,
        sort: p.sort,
      });
    }

    this.providers.sort((a: any, b: any) => a.sort - b.sort);

    this.policyService
      .policyAppliesToActiveUser$(PolicyType.TwoFactorAuthentication)
      .pipe(takeUntil(this.destroy$))
      .subscribe((policyAppliesToActiveUser) => {
        this.twoFactorAuthPolicyAppliesToActiveUser = policyAppliesToActiveUser;
      });

    await this.load();
  }

  async load() {
    this.loading = true;
    const providerList = await this.getTwoFactorProviders();
    providerList.data.forEach((p) => {
      this.providers.forEach((p2) => {
        if (p.type === p2.type) {
          p2.enabled = p.enabled;
        }
      });
    });
    this.evaluatePolicies();
    this.loading = false;
  }

  async enableAvailableBiometrics(): Promise<void> {
    await this.legacyFaceIdMigration;
    if (!(await checkBiometric()).isAvailable) {
      this.confirmationDialogService.notify("error", "error", "biometricNotSupported");
      return;
    }

    this.manage();
  }

  manage(): void {
    // Setup was triggered from the login page, which already verified the user's password
    // moments ago — skip asking for it again. Consumed and passed as dialog data (rather than
    // assigned on componentInstance after open()) so it's available before the dialog
    // component's own ngOnInit runs, instead of depending on Angular's change-detection timing.
    const preVerifiedHash = this.isMobileFirstSetup
      ? this.biometricSetupHandoff.consumeHash()
      : null;
    const dialogRef = this.dialog.open(TwoFactorWebAuthnComponent, {
      ...mobileSlideInConfig("no-background-dialog"),
      viewContainerRef: this.webAuthnModalRef,
      disableClose: true,
      data: { preVerifiedHash } as TwoFactorWebAuthnDialogData,
    });
    this.biometricSetupDialog = dialogRef;
    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((success) => {
        this.biometricSetupDialog = undefined;
        if (success) {
          this.enableToggle = true;
          void this.syncBiometricStateToServer();
          if (this.isMobileFirstSetup) {
            this.logService.info("Biometric setup successful, redirecting...");
            this.router.navigate(["/login"]);
          }
        }
      });
  }

  protected getTwoFactorProviders() {
    return this.apiService.getTwoFactorProviders();
  }

  protected filterProvider(type: TwoFactorProviderType) {
    return type === TwoFactorProviderType.OrganizationDuo;
  }

  protected async openDialog(ref: ViewContainerRef, component: ComponentType<any>) {
    this.dialog.open(component, {
      ...mobileSlideInConfig("no-background-dialog"),
      viewContainerRef: ref,
      disableClose: true,
    });
  }

  protected async openModal<T>(ref: ViewContainerRef, type: Type<T>): Promise<T> {
    const [modal, childComponent] = await this.modalService.openViewRef(type, ref);
    this.modal = modal;

    return childComponent;
  }

  protected updateStatus(enabled: boolean, type: TwoFactorProviderType) {
    if (!enabled && this.modal != null) {
      this.modal.close();
    }
    this.providers.forEach((p) => {
      if (p.type === type) {
        p.enabled = enabled;
      }
    });
    this.evaluatePolicies();
  }

  private async evaluatePolicies() {
    if (this.organizationId == null && this.providers.filter((p) => p.enabled).length === 1) {
      this.showPolicyWarning = this.twoFactorAuthPolicyAppliesToActiveUser;
    } else {
      this.showPolicyWarning = false;
    }
  }

  goBackToVault() {
    if (this.isMobileFirstSetup) {
      this.router.navigate(["/login"]);
    } else {
      this.router.navigate(["/settings"]);
    }
  }

  /**
   * Desktop can't enable biometrics on itself — only disable an existing credential. The
   * template binds toggle-switch's `disabled` to this, which is what actually blocks the enable
   * click and key press; the `isMobileDevice` guard in onEnableBiometrics is only a fallback.
   */
  get biometricEnableRestricted(): boolean {
    return !this.isMobileDevice && !this.enableToggle;
  }

  async onEnableBiometrics(isEnabled: boolean) {
    await this.legacyFaceIdMigration;
    if (!isEnabled) {
      const confirmed = await this.confirmationDialogService.confirmFor(
        ConfirmationEnum.turnOffBiometrics,
      );
      if (confirmed) {
        const deleted = await this.deleteCredentialSafely(
          deleteCredentials,
          "Failed to delete biometric credentials",
        );
        if (!deleted) {
          this.enableToggle = true;
          return;
        }
        await this.stateService.setBiometricFingerprintValidated(false);
        this.enableToggle = false;
        void this.syncBiometricStateToServer();
      } else {
        this.enableToggle = true;
      }
      return;
    }

    if (!this.isMobileDevice) {
      this.enableToggle = false;
      return;
    }

    // Same hardware-availability guard as the onboarding CTA, so a device without an
    // enrolled biometric sensor is told up front instead of after the master-password step.
    await this.enableAvailableBiometrics();
  }

  /**
   * Push this device's current biometric state to gloss-api, so Desktop's "other devices" list
   * (Task 5) can see and force-disable it. Best-effort — a failure here must not block the local
   * toggle, which remains the source of truth for whether biometric auth actually works on this
   * device.
   */
  private async syncBiometricStateToServer(): Promise<void> {
    if (!this.isMobileDevice) {
      return;
    }
    try {
      await this.loginDeviceApi.putDeviceBiometricStatus({
        biometricEnabled: this.enableToggle,
        deviceType: this.deviceType,
      });
    } catch (e) {
      this.logService.warning(`Failed to sync biometric device state: ${e}`);
    }
  }

  /**
   * Reconcile local Capacitor keychain state against gloss-api's per-device flag on load, so a
   * Desktop-initiated remote disable (Task 5) actually clears the local credential here instead
   * of only flipping a DB flag nobody re-checks. See resolveBiometricReconcileAction for the
   * decision logic.
   */
  private async reconcileBiometricStateWithServer(): Promise<void> {
    if (!this.isMobileDevice || !this.enableToggle) {
      return;
    }
    try {
      const decoded = await this.tokenService.decodeToken();
      const currentDeviceId = decoded?.device;
      if (!currentDeviceId) {
        return;
      }

      const devices = await this.loginDeviceApi.getDevices();
      const currentDevice = devices.find((d) => d.deviceId === currentDeviceId);

      switch (resolveBiometricReconcileAction(currentDevice)) {
        case "push-baseline":
          await this.syncBiometricStateToServer();
          break;
        case "clear-local":
          await this.clearRemotelyDisabledLocalBiometrics();
          break;
        case "no-op":
          break;
      }
    } catch (e) {
      this.logService.warning(`Failed to reconcile biometric device state: ${e}`);
    }
  }

  private async clearRemotelyDisabledLocalBiometrics(): Promise<void> {
    if (!this.enableToggle) {
      return;
    }
    const deleted = await this.deleteCredentialSafely(
      deleteCredentials,
      "Failed to clear remotely-disabled biometric credentials",
    );
    if (deleted) {
      await this.stateService.setBiometricFingerprintValidated(false);
      this.enableToggle = false;
    }
  }

  /**
   * Desktop-only: load every other device's biometric row so the "other devices" list can show
   * status and offer a remote disable. This device's own row (if any) is excluded — it's shown
   * separately above via the enable/disable toggles.
   */
  private async loadOtherDevices(): Promise<void> {
    if (this.isMobileDevice) {
      return;
    }
    try {
      const decoded = await this.tokenService.decodeToken();
      const devices = await this.loginDeviceApi.getDevices();
      this.otherDevices = filterOtherDevices(devices, decoded?.device);
    } catch (e) {
      this.logService.warning(`Failed to load other devices: ${e}`);
      this.otherDevices = [];
    }
  }

  /**
   * The switch flips itself on click; once the handler settles, show the real state again - a
   * cancelled confirmation or a failed request leaves the bound value unchanged, which wouldn't
   * flip it back on its own. Enabling settles only when the setup dialog it opens has closed.
   */
  async onBiometricToggle(isEnabled: boolean, toggle: ToggleSwitchComponent): Promise<void> {
    try {
      await this.onEnableBiometrics(isEnabled);
      if (this.biometricSetupDialog) {
        await firstValueFrom(this.biometricSetupDialog.afterClosed());
      }
    } finally {
      toggle.resync(this.enableToggle);
    }
  }

  async onOtherDeviceBiometricToggle(
    device: LoginDeviceResponse,
    isEnabled: boolean,
    toggle: ToggleSwitchComponent,
  ): Promise<void> {
    try {
      await this.onDisableOtherDeviceBiometrics(device, isEnabled);
    } finally {
      toggle.resync(device.biometricEnabled);
    }
  }

  /**
   * Disable-only: the toggle for another device can only turn biometrics off (Desktop cannot
   * enable biometrics on a device it isn't running on). Clicks that would turn it on are ignored
   * here as a safety net — the template already disables the switch for that case.
   */
  async onDisableOtherDeviceBiometrics(
    device: LoginDeviceResponse,
    isEnabled: boolean,
  ): Promise<void> {
    if (isEnabled) {
      return;
    }
    const confirmed = await this.confirmationDialogService.confirmFor(
      ConfirmationEnum.turnOffBiometricsOnOtherDevice,
    );
    if (!confirmed) {
      device.biometricEnabled = true;
      return;
    }
    try {
      await this.loginDeviceApi.disableDeviceBiometric(device.deviceId);
      device.biometricEnabled = false;
    } catch (e) {
      device.biometricEnabled = true;
      this.logService.error(`Failed to disable biometrics on device ${device.deviceId}: ${e}`);
    }
  }

  /**
   * Deletes a native credential, treating "not found" (nothing to delete) as success.
   * Any other error is logged and surfaced as a failure so the caller leaves local state
   * untouched, since the credential wasn't actually removed.
   */
  private async deleteCredentialSafely(
    deleteFn: () => Promise<void>,
    failureMessage: string,
  ): Promise<boolean> {
    try {
      await deleteFn();
      return true;
    } catch (e: any) {
      if (e?.message?.toLowerCase().includes("not found")) {
        return true;
      }
      this.logService.error(`${failureMessage}: ${e}`);
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async migrateLegacyFaceIdCredentialIfNeeded(): Promise<void> {
    if (!this.isMobileDevice) {
      return;
    }
    try {
      if (await this.stateService.getLegacyFaceIdMigrated()) {
        return;
      }
      await migrateLegacyFaceIdCredential();
      await this.stateService.setLegacyFaceIdMigrated(true);
    } catch (e) {
      this.logService.error(String(e));
    }
  }

  private async detectBiometricIcon(): Promise<void> {
    if (!this.isMobileDevice) {
      return;
    }
    try {
      const { biometryType } = await checkBiometric();
      this.biometricIcon = biometryTypeToIcon(biometryType);
    } catch (e) {
      this.logService.error(String(e));
    }
  }
}
